import axios, { AxiosResponse } from 'axios';

import request, { clientType, clientVersion } from '@/api/request';
import { CosmeticRequest, UserAchievementsResponse } from '@/interfaces/cosmetics';
import { CreditCardAddRequest, CreditCardResponse } from '@/interfaces/deposit';
import {
  OAuthAuthenticatorsResponse,
  OAuthTokenResponse,
  UserCosmeticUpdateResponse,
  UserLoginRequest,
  UserPaymentMethodsResponse,
  UserProfileResponse,
  UserReferralStatsResponse,
  UserRegistrationAuth0Response,
  UserRegistrationRequest,
} from '@/interfaces/user';
import { auth0Service } from '@/services/auth/auth0-service';
import eventLogger from '@/utilities/analytics';
import { apiErrorTracking, AppError } from '@/utilities/errors';

// We re-export the following functions so that we don't break
// existing code that imports them from this file, while we
// migrate towards using the new service pattern.
// Eventually, we'll remove these re-exports and update all modules
// that import them to import from a new path or use a service.
export { getUserBonusWallet } from './get-user-bonus-wallet';
export { getUserData } from './get-user-data';
export { getDepositSettings } from './get-deposit-settings';
export { getDepositPaymentMethods } from './get-deposit-payment-methods';
export { getPaymentMethod } from './get-payment-method';
export { getUserExperiences } from './get-user-experiences';

export const registerUserAuth0 = async (
  userRegistrationObject: UserRegistrationRequest
): Promise<AxiosResponse<UserRegistrationAuth0Response>> => {
  try {
    const response = await axios.request({
      url: `${process.env.AUTH0_DOMAIN}/dbconnections/signup`,
      method: 'POST',
      data: {
        client_id: process.env.AUTH0_CLIENT_ID,
        connection: 'Underdog-Database',
        email: userRegistrationObject.user.email,
        password: userRegistrationObject.user.password,
        username: userRegistrationObject.user.username,
        user_metadata: {
          promotion_id: userRegistrationObject.user.promotion_id,
          referring_link: userRegistrationObject.user.referring_link,
        },
      },
    });
    eventLogger({
      gtm: {
        eventName: 'complete_registration',
        eventObject: {
          userId: response?.data?.app_metadata?.ud_player_id,
        },
      },
    });
    return response;
  } catch (e) {
    // handle auth0 error message
    throw new AppError({
      ...e,
      title: e?.response?.data?.error?.title || 'Something went wrong',
      message: e?.response?.data?.error?.error_detail || e?.message,
    });
  }
};

export const loginUserAuth0 = async ({
  userLoginObject,
  deviceId,
}: {
  userLoginObject: UserLoginRequest;
  deviceId: string;
}): Promise<AxiosResponse<OAuthTokenResponse>> => {
  try {
    const response = await axios.request({
      method: 'POST',
      url: `${process.env.AUTH0_DOMAIN}/oauth/token`,
      data: {
        accepted_product_policies: userLoginObject.accepted_product_policies,
        audience: process.env.AUTH0_AUDIENCE,
        birthdate: userLoginObject.user.birthdate,
        client_id: process.env.AUTH0_CLIENT_ID,
        grant_type: 'password',
        password: userLoginObject.user.password,
        scope: 'offline_access',
        username: userLoginObject.user.email, // send email even though property is username
        ud_client_type: clientType,
        ud_client_version: clientVersion,
        ud_device_id: deviceId,
      },
    });

    if (response?.data?.access_token) {
      auth0Service.setAccessToken(response.data.access_token);
      auth0Service.setRefreshToken(response.data.refresh_token);
      auth0Service.setAccessTokenExpiration(response.data.expires_in);
    }

    return response;
  } catch (e) {
    // handle auth0 error message
    throw new AppError({
      ...e,
      title: e?.response?.data?.error || 'Something went wrong',
      message: e?.response?.data?.error_description || e?.message,
      mfaToken: e?.response?.data?.mfa_token,
    });
  }
};

export const mfaChallenge = async ({ mfaToken }: { mfaToken: string }): Promise<string> => {
  try {
    const response = await axios.request({
      method: 'POST',
      url: `${process.env.AUTH0_DOMAIN}/mfa/challenge`,
      data: {
        client_id: process.env.AUTH0_CLIENT_ID,
        challenge_type: 'oob',
        mfa_token: mfaToken,
      },
    });

    return response?.data?.oob_code;
  } catch (e) {
    throw new AppError({
      ...e,
      title: e?.response?.data?.error || 'Something went wrong',
      message: e?.response?.data?.error_description || e?.message,
    });
  }
};

export const getAuthenticators = async ({
  mfaToken,
}: {
  mfaToken: string;
}): Promise<OAuthAuthenticatorsResponse[]> => {
  const response = await axios.request({
    method: 'GET',
    headers: {
      Authorization: `Bearer ${mfaToken}`,
    },
    url: `${process.env.AUTH0_DOMAIN}/mfa/authenticators`,
  });

  return response?.data;
};

export const confirmMfaCode = async ({
  bindingCode,
  mfaToken,
  oobCode,
}: {
  bindingCode: string;
  mfaToken: string;
  oobCode: string;
}): Promise<AxiosResponse<OAuthTokenResponse>> => {
  try {
    const response = await axios.request({
      method: 'POST',
      url: `${process.env.AUTH0_DOMAIN}/oauth/token`,
      data: {
        audience: process.env.AUTH0_AUDIENCE,
        binding_code: bindingCode,
        client_id: process.env.AUTH0_CLIENT_ID,
        grant_type: 'http://auth0.com/oauth/grant-type/mfa-oob',
        mfa_token: mfaToken,
        oob_code: oobCode,
        scope: 'offline_access',
      },
    });

    if (response?.data?.access_token) {
      auth0Service.setAccessToken(response.data.access_token);
      auth0Service.setRefreshToken(response.data.refresh_token);
      auth0Service.setAccessTokenExpiration(response.data.expires_in);
    }

    return response;
  } catch (e) {
    // handle auth0 error message
    throw new AppError({
      ...e,
      title: e?.response?.data?.error || 'Something went wrong',
      message: e?.response?.data?.error_description || e?.message,
    });
  }
};

export const logoutUser = async (): Promise<AxiosResponse<void>> => {
  const response = await request({
    method: 'DELETE',
    url: '/users/sign_out',
  });

  return response;
};

export const changePasswordAuth0 = async ({
  email,
}: {
  email: string;
}): Promise<AxiosResponse<string>> => {
  const response = await axios.request({
    method: 'POST',
    url: `${process.env.AUTH0_DOMAIN}/dbconnections/change_password`,
    data: {
      client_id: process.env.AUTH0_CLIENT_ID,
      email,
      connection: 'Underdog-Database',
    },
  });

  return response;
};

export const getUserReferralStats = async (): Promise<AxiosResponse<UserReferralStatsResponse>> => {
  const response = await request({
    method: 'GET',
    url: '/user/referral_stats',
  });

  return response;
};

export const getUserProfile = async (): Promise<AxiosResponse<UserProfileResponse>> => {
  const response = await request({
    method: 'GET',
    url: '/user/profile',
  });

  return response;
};

export const getUserAchievements = async (): Promise<AxiosResponse<UserAchievementsResponse>> => {
  const response = await request({
    method: 'GET',
    url: '/user/achievements',
  });

  return response;
};

export const setUserCosmetics = async (
  cosmetic: CosmeticRequest
): Promise<AxiosResponse<UserCosmeticUpdateResponse>> => {
  const response = await request({
    method: 'POST',
    url: '/user/cosmetics',
    data: {
      cosmetic,
    },
  });

  return response;
};

/**
 *
 * Add and retrieve Paysafe user credit cards
 */
export const getUserPaymentMethods = async (): Promise<
  AxiosResponse<UserPaymentMethodsResponse>
> => {
  try {
    const response = await request({
      method: 'GET',
      underdogAPIVersion: 'v2',
      url: '/user/payment_methods',
      requiresLatLong: true,
    });

    return response;
  } catch (e) {
    apiErrorTracking(e);
    throw new AppError(e);
  }
};

export const addCreditCard = async ({
  depositObject,
}: {
  depositObject: CreditCardAddRequest;
}): Promise<AxiosResponse<CreditCardResponse>> => {
  try {
    const response = await request({
      data: {
        credit_card: {
          description: depositObject.description,
          expiration: depositObject.expiration,
          first_name: depositObject.firstName,
          last_name: depositObject.lastName,
          token: depositObject.token,
          zip: depositObject.zip,
        },
      },
      method: 'POST',
      requiresLatLong: true,
      url: '/user/credit_cards',
    });

    return response;
  } catch (e) {
    apiErrorTracking(e);
    throw new AppError(e);
  }
};

export const removeCreditCard = async ({
  creditCardId,
}: {
  creditCardId: string;
}): Promise<AxiosResponse<void>> => {
  try {
    const response = await request({
      method: 'DELETE',
      underdogAPIVersion: 'v3',
      url: `/user/credit_cards/${creditCardId}`,
      requiresLatLong: true,
    });

    return response;
  } catch (e) {
    apiErrorTracking(e);
    throw new AppError(e);
  }
};

export const removePaymentMethod = async ({
  paymentMethodId,
}: {
  paymentMethodId: string;
}): Promise<AxiosResponse<void>> => {
  try {
    const response = await request({
      method: 'DELETE',
      url: `/user/payment_methods/${paymentMethodId}`,
      requiresLatLong: true,
    });

    return response;
  } catch (e) {
    apiErrorTracking(e);
    throw new AppError(e);
  }
};

export const claimAirdrop = async ({
  airdropId,
}: {
  airdropId: string;
}): Promise<AxiosResponse<void>> => {
  const response = await request({
    method: 'POST',
    url: '/user/airdrop_offers/claims',
    data: {
      id: airdropId,
    },
  });
  return response;
};
